"use client"

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ChevronDown, ChevronUp, Trash2, Minus, Plus } from "lucide-react"
import { useCurrencySymbol } from "@/hooks/shared/useCurrencySymbol"

interface ServiceStatusPanelProps {
  serviceName: string
  specialist: string
  quantity: number
  price: number
  discount: number
  toPay: number
  onQuantityChange?: (quantity: number) => void
  onPriceChange?: (price: number) => void
  onDiscountChange?: (discount: number) => void
  onDelete?: () => void
}

export function ServiceStatusPanel({
  serviceName,
  specialist,
  quantity,
  price,
  discount,
  toPay,
  onQuantityChange,
  onPriceChange,
  onDiscountChange,
  onDelete,
}: ServiceStatusPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { t } = useTranslation()
  const currencySymbol = useCurrencySymbol()

  return (
    <div
      className="overflow-hidden"
      style={{
        borderRadius: "var(--border-radius-m)",
        border: "1px solid var(--color-border-neutral-faded)",
        backgroundColor: "var(--color-background-base)",
      }}
    >
      {/* Collapsed Header */}
      <div className="flex items-center">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 text-left"
          style={{
            padding: "var(--spacing-200)",
            transition: "background-color 0.2s",
          }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div
                className="text-[13px] font-normal"
                style={{ color: "var(--color-foreground-neutral-faded)", marginBottom: "var(--spacing-100)" }}
              >
                {specialist}
              </div>
              <div
                className="text-[15px] font-normal leading-snug"
                style={{ color: "var(--color-foreground-neutral)" }}
              >
                {serviceName}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[17px] font-semibold" style={{ color: "var(--color-foreground-neutral)" }}>
                {toPay} {currencySymbol}
              </span>
              {isExpanded ? (
                <ChevronUp className="h-5 w-5" style={{ color: "var(--color-foreground-neutral-faded)" }} />
              ) : (
                <ChevronDown className="h-5 w-5" style={{ color: "var(--color-foreground-neutral-faded)" }} />
              )}
            </div>
          </div>
        </button>

        <button
          onClick={onDelete}
          disabled={!onDelete}
          className="h-full px-3 flex items-center justify-center shrink-0 disabled:opacity-40"
          style={{
            borderLeft: "1px solid var(--color-border-neutral-faded)",
            color: "var(--color-foreground-neutral-faded)",
          }}
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div
          style={{
            padding: "var(--spacing-200)",
            paddingTop: 0,
            borderTop: "1px solid var(--color-border-neutral-faded)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-200)" }}>
            {/* Quantity and Price Row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  className="text-[13px] font-normal block"
                  style={{ color: "var(--color-foreground-neutral-faded)", marginBottom: "var(--spacing-100)" }}
                >
                  {t("appointment.payment.quantity", "Qty")}
                </label>
                <div className="input-like">
                  <button
                    onClick={() => onQuantityChange?.(Math.max(1, quantity - 1))}
                    type="button"
                    className="input-like__btn"
                    aria-label={t("common.decrease", "Decrease quantity")}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="text-[17px] font-normal" style={{ color: "var(--color-foreground-neutral)" }}>
                    {quantity}
                  </span>
                  <button
                    onClick={() => onQuantityChange?.(quantity + 1)}
                    type="button"
                    className="input-like__btn"
                    aria-label={t("common.increase", "Increase quantity")}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <label
                  className="text-[13px] font-normal block"
                  style={{ color: "var(--color-foreground-neutral-faded)", marginBottom: "var(--spacing-100)" }}
                >
                  {t("appointment.payment.price_byn", "Price")}
                </label>
                <div className="input-like">
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => onPriceChange?.(Number(e.target.value))}
                    className="input-like__field"
                  />
                </div>
              </div>
            </div>

            {/* Discount and To Pay Row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  className="text-[13px] font-normal block"
                  style={{ color: "var(--color-foreground-neutral-faded)", marginBottom: "var(--spacing-100)" }}
                >
                  {t("appointment.payment.discount_percent", "Discount, %")}
                </label>
                <div className="input-like">
                  <input
                    type="number"
                    value={discount}
                    onChange={(e) => onDiscountChange?.(Number(e.target.value))}
                    className="input-like__field"
                  />
                </div>
              </div>

              <div>
                <label
                  className="text-[13px] font-normal block"
                  style={{ color: "var(--color-foreground-neutral-faded)", marginBottom: "var(--spacing-100)" }}
                >
                  {t("appointment.payment.to_pay_byn", "To pay")}
                </label>
                <div className="input-like input-like--muted">
                  <input
                    type="number"
                    value={toPay}
                    readOnly
                    className="input-like__field"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
